/**
 * Section Row Component
 * Displays a single section in the sections list table
 */

import { Edit2 } from 'lucide-react';
import React from 'react';
import { useAppSelector } from '../../../hooks/useRedux';
import { ONBOARDING_TOUR_TARGETS } from '../../../services/onboardingTourService';
import { selectEffectiveOutlet } from '../../../store/outlets';
import { OutletSection } from '../../../types/section';

interface SectionRowProps {
  canManage?: boolean;
  hasPrinterConfig: boolean;
  isTourPrimary?: boolean;
  section: OutletSection;
  showOutletColumn: boolean;
  onEdit: (section: OutletSection) => void;
  onConfigurePrinters: (section: OutletSection) => void;
  onViewTables: (section: OutletSection) => void;
}

export const SectionRow: React.FC<SectionRowProps> = ({
  canManage = true,
  hasPrinterConfig,
  isTourPrimary = false,
  section,
  showOutletColumn,
  onEdit,
  onConfigurePrinters,
  onViewTables,
}) => {
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-gray-900 uppercase">
          {section.name}
        </span>
      </td>
      {showOutletColumn ? (
        <td className="px-4 py-3">
          <span className="text-sm text-gray-600">
            {section.outlet_name || selectedOutlet?.name || '—'}
          </span>
        </td>
      ) : null}
      <td className="px-4 py-3 text-center">
        <button
          type="button"
          onClick={() => onViewTables(section)}
          data-onboarding-tour={
            isTourPrimary
              ? ONBOARDING_TOUR_TARGETS.sectionViewTablesButton
              : undefined
          }
          className="text-sm text-pos-primary hover:text-pos-secondary font-medium transition-colors"
        >
          VIEW
        </button>
      </td>
      <td className="px-4 py-3 text-center">
        <button
          type="button"
          onClick={() => onConfigurePrinters(section)}
          data-onboarding-tour={
            isTourPrimary
              ? ONBOARDING_TOUR_TARGETS.sectionConfigurePrintersButton
              : undefined
          }
          className="inline-flex items-center gap-2 text-sm font-medium text-pos-primary transition-colors hover:text-pos-secondary"
        >
          <span
            className={`inline-block h-2 w-2 rounded-full ${
              hasPrinterConfig ? 'bg-green-500' : 'bg-gray-300'
            }`}
          />
          VIEW
        </button>
      </td>
      <td className="px-4 py-3 text-center">
        <span
          className={`text-sm font-medium ${
            section.is_hidden ? 'text-red-500' : 'text-green-500'
          }`}
        >
          {section.is_hidden ? 'HIDDEN' : 'YES'}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        {canManage ? (
          <button
            type="button"
            onClick={() => onEdit(section)}
            className="p-1.5 text-gray-400 hover:text-pos-primary hover:bg-pos-light/30 rounded-lg transition-colors"
            title="Edit section"
          >
            <Edit2 size={16} />
          </button>
        ) : null}
      </td>
    </tr>
  );
};
